import React, { useState } from "react";
import "./VolumeMomentum.scss";
import { CompanyAnalytics } from "../../../services/analyticsService";
import analyticsService from "../../../services/analyticsService";

interface VolumeMetric {
  metric: string;
  value: string | number;
  whyImportant: string;
  positiveForBuy: string;
  positiveForSell: string;
  signal: "buy" | "sell" | "neutral";
}

interface VolumeMomentumData {
  metrics: VolumeMetric[];
  overallMomentum: "strong" | "moderate" | "weak";
}

interface VolumeMomentumProps {
  companyId: number;
  analytics: CompanyAnalytics | null;
  data?: VolumeMomentumData;
  onRefresh?: () => void;
}

const VolumeMomentum: React.FC<VolumeMomentumProps> = ({ companyId, analytics, data, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    analyticsService.refreshVolumeMomentum(companyId).subscribe({
      next: () => {
        if (onRefresh) onRefresh();
        setRefreshing(false);
      },
      error: (err) => {
        console.error("Failed to refresh volume momentum:", err);
        setRefreshing(false);
      }
    });
  };
  const getVolumeDataFromAnalytics = (): VolumeMomentumData => {
    if (!analytics?.volume) {
      return { overallMomentum: "weak", metrics: [] };
    }
    const vol = analytics.volume;
    const metrics: VolumeMetric[] = [];

    const mapSignal = (signal: string | null): "buy" | "sell" | "neutral" => {
      if (!signal) return "neutral";
      const s = signal.toLowerCase();
      if (s.includes("buy") || s === "bullish" || s === "positive" || s === "strong") return "buy";
      if (s.includes("sell") || s === "bearish" || s === "negative" || s === "weak") return "sell";
      return "neutral";
    };

    if (vol.momentum_direction) {
      metrics.push({
        metric: "Volume Trend",
        value: vol.momentum_direction.toUpperCase(),
        whyImportant: "Strength of move",
        positiveForBuy: "Rising on up days",
        positiveForSell: "Rising on down days",
        signal: vol.momentum_direction.toLowerCase() === "up" ? "buy" :
                vol.momentum_direction.toLowerCase() === "down" ? "sell" : "neutral"
      });
    }

    if (vol.vwap !== null && vol.vwap !== undefined) {
      metrics.push({
        metric: "VWAP / Avg Price",
        value: `₹${vol.vwap.toFixed(2)}`,
        whyImportant: "Fair entry point",
        positiveForBuy: "Price > VWAP",
        positiveForSell: "Price < VWAP",
        signal: mapSignal(vol.overall_momentum)
      });
    }

    if (vol.delivery_percent !== null && vol.delivery_percent !== undefined) {
      metrics.push({
        metric: "Delivery % (India)",
        value: `${vol.delivery_percent.toFixed(1)}%`,
        whyImportant: "Long-term holding intent",
        positiveForBuy: "High (>60%)",
        positiveForSell: "Low (<40%)",
        signal: mapSignal(vol.delivery_signal)
      });
    }

    const mapMomentum = (momentum: string | null): "strong" | "moderate" | "weak" => {
      if (!momentum) return "weak";
      const m = momentum.toLowerCase();
      if (m.includes("strong") || m.includes("high")) return "strong";
      if (m.includes("moderate") || m.includes("medium")) return "moderate";
      return "weak";
    };

    return {
      metrics,
      overallMomentum: mapMomentum(vol.overall_momentum)
    };
  };

  const mockData: VolumeMomentumData = {
    overallMomentum: "strong",
    metrics: [
      {
        metric: "Volume Trend",
        value: "Rising on Up Days",
        whyImportant: "Strength of move",
        positiveForBuy: "Rising on up days",
        positiveForSell: "Rising on down days",
        signal: "buy"
      },
      {
        metric: "VWAP / Avg Price",
        value: "₹832.50",
        whyImportant: "Fair entry point",
        positiveForBuy: "Price > VWAP",
        positiveForSell: "Price < VWAP",
        signal: "buy"
      },
      {
        metric: "Delivery % (India)",
        value: "68%",
        whyImportant: "Long-term holding intent",
        positiveForBuy: "High (>60%)",
        positiveForSell: "Low",
        signal: "buy"
      }
    ]
  };

  const volumeData = data || (analytics ? getVolumeDataFromAnalytics() : mockData);

  const getSignalBadge = (signal: string) => {
    switch (signal) {
      case "buy":
        return <span className="tag is-success">Buy</span>;
      case "sell":
        return <span className="tag is-danger">Sell</span>;
      case "neutral":
        return <span className="tag is-warning">Neutral</span>;
      default:
        return null;
    }
  };

  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case "buy":
        return <span className="icon has-text-success"><i className="fas fa-arrow-up"></i></span>;
      case "sell":
        return <span className="icon has-text-danger"><i className="fas fa-arrow-down"></i></span>;
      case "neutral":
        return <span className="icon has-text-warning"><i className="fas fa-minus"></i></span>;
      default:
        return null;
    }
  };

  const getMomentumBadge = (momentum: string) => {
    switch (momentum) {
      case "strong":
        return <span className="tag is-success is-large">Strong Momentum</span>;
      case "moderate":
        return <span className="tag is-warning is-large">Moderate Momentum</span>;
      case "weak":
        return <span className="tag is-danger is-large">Weak Momentum</span>;
      default:
        return null;
    }
  };

  const getMomentumClass = (momentum: string) => {
    switch (momentum) {
      case "strong":
        return "is-success";
      case "moderate":
        return "is-warning";
      case "weak":
        return "is-danger";
      default:
        return "is-info";
    }
  };

  return (
    <div className="volume-momentum">
      <div className="box">
        <div className="level mb-5">
          <div className="level-left">
            <div className="level-item">
              <div>
                <h2 className="title is-4 mb-2">Volume & Momentum</h2>
                <p className="subtitle is-6 has-text-grey">
                  Confirms conviction behind price movements
                </p>
              </div>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <button
                className={`button is-small is-info mr-3 ${refreshing ? 'is-loading' : ''}`}
                onClick={handleRefresh}
                disabled={refreshing}
                title="Refresh volume and momentum"
              >
                <span className="icon is-small">
                  <i className="fas fa-sync-alt"></i>
                </span>
                <span>Refresh</span>
              </button>
            </div>
            <div className="level-item">
              <div className="momentum-badge-card">
                <p className="heading mb-3">Overall Momentum</p>
                <div className="has-text-centered">
                  {getMomentumBadge(volumeData.overallMomentum)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="columns is-variable is-4">
          {volumeData.metrics.map((metric, index) => (
            <div key={index} className="column is-4">
              <div className={`card metric-card ${metric.signal}`}>
                <div className="card-content">
                  <div className="media mb-3">
                    <div className="media-left">
                      {getSignalIcon(metric.signal)}
                    </div>
                    <div className="media-content">
                      <p className="title is-6 mb-1">{metric.metric}</p>
                      <p className="subtitle is-7 has-text-grey">{metric.whyImportant}</p>
                    </div>
                  </div>

                  <div className="metric-value mb-4">
                    <p className="title is-4 has-text-centered">{metric.value}</p>
                  </div>

                  <div className="signal-indicators">
                    <div className="field">
                      <div className="control">
                        <div className="tags has-addons mb-2">
                          <span className="tag is-light">Buy Signal:</span>
                          <span className="tag is-success is-light">{metric.positiveForBuy}</span>
                        </div>
                      </div>
                    </div>
                    <div className="field">
                      <div className="control">
                        <div className="tags has-addons">
                          <span className="tag is-light">Sell Signal:</span>
                          <span className="tag is-danger is-light">{metric.positiveForSell}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="has-text-centered mt-4">
                    {getSignalBadge(metric.signal)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="volume-summary mt-5">
          <div className="notification is-light">
            <div className="columns is-mobile is-vcentered">
              <div className="column">
                <p className="heading">Volume Conviction</p>
                <p className="title is-5">
                  {volumeData.metrics.filter(m => m.signal === "buy").length} out of{" "}
                  {volumeData.metrics.length} indicators are bullish
                </p>
              </div>
              <div className="column is-narrow">
                <div className={`conviction-meter ${getMomentumClass(volumeData.overallMomentum)}`}>
                  <span className="icon is-large">
                    <i className="fas fa-3x fa-chart-line"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolumeMomentum;
